// --- Our Modules --------------------------------------------------------------------------------
import type { TyLogSeverity } from "@/types";

// --- Logic --------------------------------------------------------------------------------------

/**
 * Colour follows severity, not the status code. Red is reserved for things that are actually broken,
 * so a screen full of expired tokens no longer looks like an outage.
 */
export const SEVERITY_TONES: Record<TyLogSeverity, string> = {
    success: "good",
    rejected: "warn",
    error: "bad",
};

export function formatDateTime(value: string, lang: string): string {
    return new Date(value).toLocaleString(lang === "en" ? "en-GB" : "ar-EG", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/** Axis labels stay short — a full timestamp under every bar is unreadable at any width. */
export function formatBucket(value: string, groupBy: "hour" | "day", lang: string): string {
    const date = new Date(value);
    const locale = lang === "en" ? "en-GB" : "ar-EG";
    if (groupBy === "hour") return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Strips the query string for display. The full URL is still on the detail screen — a list row that
 * wraps onto three lines because of a filter payload is harder to scan, not more informative.
 */
export function shortPath(url: string): string {
    return url.split("?")[0];
}

/** Drops empty values so a cleared filter disappears from the query string instead of sending "". */
export function compactQuery(source: Record<string, string | number | undefined>): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(source)) {
        if (value !== undefined && value !== "" && value !== null) out[key] = value;
    }
    return out;
}
